import React, { useState, useRef, useEffect } from 'react';
import { getSuggestions, getRxCUI, getDosages, reorderForQuery } from '../../services/rxnavService';
import { lbl, inp, NoneCheckbox } from './shared';

const FREQUENCIES = [
  'Once daily (QD)',
  'Twice daily (BID)',
  'Three times daily (TID)',
  'Four times daily (QID)',
  'Every 4 hours',
  'Every 6 hours',
  'Every 8 hours',
  'Every 12 hours',
  'As needed (PRN)',
  'Weekly',
  'Monthly',
];

const sel = {
  ...inp,
  cursor: 'pointer',
  appearance: 'auto',
};

// Strips the drug name prefix from an RxNav dosage string
// e.g. "Lisinopril 10 MG Oral Tablet" → "10 MG Oral Tablet"
function dosageLabel(drugName, dosageStr) {
  if (!dosageStr) return dosageStr;
  const lower = dosageStr.toLowerCase();
  const prefix = (drugName || '').toLowerCase();
  return lower.startsWith(prefix) ? dosageStr.slice(drugName.length).trim() : dosageStr;
}

export default function Medications({ data, onChange }) {
  const [query, setQuery]               = useState('');
  const [suggestions, setSuggestions]   = useState([]);
  const [drug, setDrug]                 = useState(null);
  const [dosages, setDosages]           = useState([]);
  const [selectedDosage, setDosage]           = useState('');
  const [selectedDosageRxcui, setDosageRxcui] = useState('');
  const [selectedBrandRxcuis, setBrandRxcuis] = useState([]);
  const [selectedFreq, setFreq]         = useState('');
  const [loadingDrug, setLoadingDrug]   = useState(false);
  const [loadingDosage, setLoadingDosage] = useState(false);
  const [manualMode, setManualMode]     = useState(false);
  const [manualName, setManualName]     = useState('');
  const [manualStrength, setManualStrength] = useState('');
  const [manualFreq, setManualFreq]     = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const meds = data.medications || [];
  const canAdd = drug && selectedDosage && selectedFreq;

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (query.length < 2) { setSuggestions([]); return; }
    setSuggestions(getSuggestions(query));
  }, [query]);

  async function handleSelectDrug(name) {
    setSuggestions([]);
    setQuery('');
    setDosage('');
    setDosageRxcui('');
    setFreq('');
    setLoadingDrug(true);
    try {
      const rxcui = await getRxCUI(name);
      setDrug({ name, rxcui });
      if (rxcui) {
        setLoadingDosage(true);
        const options = await getDosages(name, rxcui);
        setDosages(options);
        setLoadingDosage(false);
      } else {
        setDosages([]);
      }
    } catch {
      setDrug({ name, rxcui: null });
      setDosages([]);
    }
    setLoadingDrug(false);
  }

  function handleClear() {
    setDrug(null);
    setDosages([]);
    setDosage('');
    setDosageRxcui('');
    setBrandRxcuis([]);
    setFreq('');
    setQuery('');
    setSuggestions([]);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function handleAdd() {
    onChange('medications', [...meds, {
      name: drug.name,
      rxcui: selectedDosageRxcui || drug.rxcui || '',
      brandRxcuis: selectedBrandRxcuis,
      strength: selectedDosage,
      frequency: selectedFreq,
    }]);
    handleClear();
  }

  function handleRemove(i) {
    onChange('medications', meds.filter((_, idx) => idx !== i));
  }

  function handleAddManual(e) {
    e.preventDefault();
    if (!manualName.trim()) return;
    onChange('medications', [...meds, {
      name: manualName.trim(),
      rxcui: '',
      strength: manualStrength.trim(),
      frequency: manualFreq,
    }]);
    setManualName('');
    setManualStrength('');
    setManualFreq('');
    setManualMode(false);
  }

  return (
    <div>
      {/* ── Instructions ── */}
      <div style={{
        background: '#f4f8fc', border: '1.5px solid #dbeef8',
        borderRadius: 8, padding: '12px 16px', marginBottom: 16,
        fontSize: 13, color: '#4a6288', lineHeight: 1.6,
      }}>
        <strong style={{ color: '#1a3364' }}>How to add medications:</strong>
        <ol style={{ margin: '6px 0 0', paddingLeft: 20 }}>
          <li>Type at least 2 letters to search for a medication and select it from the list.</li>
          <li>Choose a <strong>Dosage</strong> and <strong>Frequency</strong> from the dropdowns.</li>
          <li>Click <strong>+ Add This Medication</strong> to add it to your list. Repeat for each medication.</li>
          <li>Can't find your medication at all? Check <strong>"Can't find your medication?"</strong> below the search box to enter it by hand.</li>
        </ol>
      </div>

      {/* ── Entry panel ── */}
      <div style={{
        background: '#f4f8fc',
        border: '1.5px solid #dbeef8',
        borderRadius: 10,
        padding: '18px 18px 14px',
        marginBottom: 20,
      }}>

        {/* Drug name */}
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Medication</label>
          {drug ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8,
              padding: '10px 12px',
            }}>
              <div>
                <span style={{ fontWeight: 700, color: '#1a3364', fontSize: 15 }}>{drug.name}</span>
                {drug.rxcui && (
                  <span style={{ fontSize: 12, color: '#7a99b8', marginLeft: 8 }}>RxCUI: {drug.rxcui}</span>
                )}
              </div>
              <button type="button" onClick={handleClear} style={{
                padding: '5px 12px', border: '1.5px solid #c5d5e8', borderRadius: 6,
                background: '#fff', color: '#2a5298', fontWeight: 600, fontSize: 13, cursor: 'pointer',
              }}>
                Change
              </button>
            </div>
          ) : (
            <div ref={containerRef} style={{ position: 'relative' }}>
              <input
                ref={inputRef}
                style={{ ...inp, background: '#fff' }}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Type a medication name…"
                autoComplete="off"
                disabled={loadingDrug}
              />
              {loadingDrug && (
                <div style={{ fontSize: 13, color: '#7a99b8', marginTop: 4 }}>Loading…</div>
              )}
              {suggestions.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8,
                  zIndex: 200, maxHeight: 200, overflowY: 'auto',
                  boxShadow: '0 6px 16px rgba(26,51,100,0.13)', marginTop: 2,
                }}>
                  {suggestions.map(s => (
                    <div key={s} onMouseDown={() => handleSelectDrug(s)}
                      style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 14, color: '#1a3364' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#dbeef8'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      {reorderForQuery(s, query)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {!drug && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="medManualMode"
                checked={manualMode}
                onChange={e => setManualMode(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1a3364' }}
              />
              <label htmlFor="medManualMode" style={{ fontSize: 13, color: '#4a6288', cursor: 'pointer' }}>
                Can't find your medication? Enter it manually
              </label>
            </div>
            {manualMode && (
              <div style={{
                background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8,
                padding: 14, marginTop: 10,
              }}>
                <div style={{ marginBottom: 10 }}>
                  <label style={lbl}>Medication Name <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(required)</span></label>
                  <input
                    style={{ ...inp, background: '#fff' }}
                    value={manualName}
                    onChange={e => setManualName(e.target.value)}
                    placeholder="e.g. Hydrocodone/Acetaminophen"
                    autoComplete="off"
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={lbl}>Strength <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <input
                    style={{ ...inp, background: '#fff' }}
                    value={manualStrength}
                    onChange={e => setManualStrength(e.target.value)}
                    placeholder="e.g. 5 MG / 325 MG"
                    autoComplete="off"
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={lbl}>Frequency <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <select
                    style={{ ...sel, background: '#fff', color: '#1a3364' }}
                    value={manualFreq}
                    onChange={e => setManualFreq(e.target.value)}
                  >
                    <option value="">Select a frequency…</option>
                    {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={handleAddManual}
                    disabled={!manualName.trim()}
                    style={{
                      padding: '9px 22px', border: 'none', borderRadius: 8,
                      background: manualName.trim() ? '#2a5298' : '#b8cfe8',
                      color: '#fff', fontWeight: 600, fontSize: 14,
                      cursor: manualName.trim() ? 'pointer' : 'default',
                    }}
                  >
                    + Add This Medication
                  </button>
                  <button
                    type="button"
                    onClick={() => setManualMode(false)}
                    style={{
                      padding: '9px 16px', border: '1.5px solid #c5d5e8', borderRadius: 8,
                      background: '#fff', color: '#4a6288', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dosage */}
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>
            Dosage
            {loadingDosage && <span style={{ fontWeight: 400, color: '#7a99b8', marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>Loading…</span>}
          </label>
          <select
            style={{ ...sel, background: '#fff', color: '#1a3364' }}
            value={selectedDosage}
            onChange={e => {
              const label = e.target.value;
              const match = dosages.find(d => d.label === label);
              setDosage(label);
              setDosageRxcui(match?.rxcui ?? '');
              setBrandRxcuis(match?.brandRxcuis ?? []);
            }}
            disabled={!drug || loadingDosage}
          >
            <option value="">{!drug ? 'Select a medication first…' : dosages.length === 0 ? 'No dosages found — type manually below' : 'Select a dosage…'}</option>
            {dosages.map(d => (
              <option key={d.rxcui || d.label} value={d.label}>{d.label}</option>
            ))}
          </select>
          {drug && dosages.length === 0 && !loadingDosage && (
            <input
              style={{ ...inp, background: '#fff', marginTop: 8 }}
              value={selectedDosage}
              placeholder="e.g. 10 mg"
              onChange={e => setDosage(e.target.value)}
            />
          )}
        </div>

        {/* Frequency */}
        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>Frequency</label>
          <select
            style={{ ...sel, background: '#fff', color: '#1a3364' }}
            value={selectedFreq}
            onChange={e => setFreq(e.target.value)}
            disabled={!drug}
          >
            <option value="">{!drug ? 'Select a medication first…' : 'Select a frequency…'}</option>
            {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>

        <button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: 8,
            background: canAdd ? '#1a3364' : '#b8cfe8',
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
            cursor: canAdd ? 'pointer' : 'default',
            transition: 'background 0.15s',
          }}
        >
          + Add This Medication
        </button>
      </div>

      {/* ── Added medications list ── */}
      {meds.length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1a3364', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            Added Medications
            <span style={{ background: '#1a3364', color: '#fff', borderRadius: '999px', padding: '1px 8px', fontSize: 12, fontWeight: 700, letterSpacing: 0, textTransform: 'none' }}>
              {meds.length}
            </span>
          </div>
          {meds.map((med, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#fff', border: '1.5px solid #dbeef8', borderRadius: 8,
              padding: '11px 14px', marginBottom: 8,
            }}>
              <div>
                <div style={{ fontWeight: 700, color: '#1a3364', fontSize: 14 }}>{med.name}</div>
                <div style={{ fontSize: 13, color: '#4a6288', marginTop: 2 }}>
                  {dosageLabel(med.name, med.strength)}
                  {med.frequency && <span style={{ color: '#7a99b8' }}> &mdash; {med.frequency}</span>}
                </div>
              </div>
              <button type="button" onClick={() => handleRemove(i)} style={{
                padding: '5px 10px', border: 'none', background: '#f5e8e8',
                borderRadius: 6, cursor: 'pointer', color: '#b03030', fontWeight: 700, fontSize: 14,
              }}>
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {meds.length === 0 && (
        <NoneCheckbox
          checked={data.noMedications}
          onChange={v => onChange('noMedications', v)}
          label="I don't take any prescription medications"
        />
      )}
    </div>
  );
}
